import { useQueryClient } from "@tanstack/react-query";
import { Badge, message } from "antd";
import { useMemo, useRef, useState } from "react";
import { Button, Col, Container, ListGroup, Row } from "react-bootstrap";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import { getEnv } from "../../AppConfigs";
import { Routes } from "../../AppRoutes";
import { Header } from "../../components/header";
import { CreateNotification } from "../../components/notifier";
import Spinner from "../../components/spinner/Spinner";
import { mailRegexs } from "../../constants/mailTemplateConstants";
import MESSAGES from "../../constants/messages";
import PERMISSIONS from "../../constants/permissions";
import { SharePointLinkType, SharePointScope } from "../../constants/sharePointConstant";
import { documentStatus, userAction } from "../../constants/statusConstants";
import { useMineInfo } from "../../contexts/mine-info-context";
import { getNextDocumentStatus } from "../../helper/dataHelper";
import { useUser } from "../../hook";
import { useSearchParams } from "../../hook/use-search-params";
import { Icon } from "../../modules/icon";
import { DocumentService } from "../../services/document/document.service";
import { FileService } from "../../services/file/file.service";
import { MailService } from "../../services/mail/mail.service";
import { PermissionService } from "../../services/permission/permission.service";
import { getListIdByStatusValue } from "../../services/serviceHelper";
import { getQueryVariable } from "../../services/util";
import { useSelector } from "../../store/store";
import { CommentList } from "./comment/comment-list";
import { DepartmentList } from "./components/document/permission-department/department-list";
import { Editor } from "./components/editor/editor";
import EditDocumentDetailInfo from "./detail-edit/edit-document-detail-info";
import { DocumentInfo } from "./detail/detail-document-info";
import { DocumentLink } from "./detail/detail-document-link";
import { DocumentProcess } from "./detail/detail-document-process";
import {
  EditorContentPermissionContextProvider,
  EditorContentPermissionContextType,
  EditorContentPermissionType,
} from "./editor-content-permission-context";
import { useDocumentHistoryInfo } from "./hooks/document-content/use-document-history-info";
import { useDocumentLinkById } from "./hooks/document-link/use-document-link";
import { useDocumentLinkedById } from "./hooks/document-link/use-document-linked";
import { useDocumentInfo } from "./hooks/documents/use-document-info";
import { useDocumentPermission } from "./hooks/documents/use-document-permission";
import AcceptRefuseProcessModal from "./modal/accept-refuse-process-modal";
import EditDocumentDetailModal from "./modal/edit-document-detail-modal";
import EditDocumentProcessModal from "./modal/edit-document-process-modal";
import { getTitleForDocument } from "./util";

export const DocumentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const [openProcessHistoryModal, setOpenProcessHistoryModal] = useState(false);
  const [openEditDocumentProcessModal, setOpenEditDocumentProcessModalModal] = useState(false);
  const [isPublish, setPublish] = useState(false);
  const [interpublishStatus, setInterpublishStatus] = useState(false);
  const statusQuery = getQueryVariable("status").replace("/", "");
  const listId = getListIdByStatusValue(statusQuery, false);
  const [isChangeApprover, setIsChangeApprover] = useState(false);
  const [openPermissionModal, setOpenPermissionModal] = useState(false);
  const [currentDocumentPermissionProcess, setCurrentDocumentPermissionProcess] = useState<any>();
  const [isInterpublish, setIsInterPublish] = useState(true);
  const [isOpenRefuseModal, setIsOpenRefuseModal] = useState(false);

  const filterScopes = [SharePointScope.users];
  const filterTypes = [SharePointLinkType.view, SharePointLinkType.edit];
  const callbackRef = useRef<Function | undefined>(undefined);
  const detailUrl = getEnv("REACT_APP_DOCUMENT_DETAILS_URL");
  const mailtemplates = useSelector((state: any) => state.systemMgmt.mailtemplates);
  const [currentStatus, setCurrentStatus] = useState("" as string);

  //Refactor
  const history = useHistory();
  const { searchParams, replaceSearchParams } = useSearchParams();
  const isPublishStatus = searchParams.get("status");
  const mode = searchParams.get("mode");
  const docParentFolderId = searchParams.get("folderId");
  const templateId = searchParams.get("templateId");
  const action = searchParams.get("action");

  const [selectedMenu, setSelectedMenu] = useState(action || userAction.file);

  const { mappedData: document, isLoading } = useDocumentInfo();
  const { data: documentHistoy } = useDocumentHistoryInfo();
  const { hasWritePermission } = useDocumentPermission();
  const queryClient = useQueryClient();
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [contentPermission, setContentPermission] = useState<EditorContentPermissionType[]>([]);
  const docId = document.docWorkingId || document.listItemId;
  const { docLinkCount } = useDocumentLinkById(docId);
  const { docLinkedCount } = useDocumentLinkedById(docId);
  const totalDocLink = docLinkCount + docLinkedCount;
  const { name, email } = useUser();

  const { checkPermission } = useMineInfo();

  const ctx = useMemo<EditorContentPermissionContextType>(
    () => ({
      contentPermissions: contentPermission,
      setContentPermission: setContentPermission,
    }),
    [contentPermission],
  );

  const onInterPublish = (e: any) => {
    setIsInterPublish(e);
  };

  const onCloseEditDocument = () => {
    if (document?.id! <= 0) {
      getBackLink();
      return;
    }
    searchParams.delete("mode");
    replaceSearchParams(searchParams);
  };

  const toggleEditDetailModal = () => {
    if (!checkPermission(PERMISSIONS.EDIT_DOCUMENT)) return message.error(MESSAGES.NOT_PERMISSION);

    searchParams.set("mode", "edit");
    replaceSearchParams(searchParams);
  };

  const toggleProcessHistoryModal = () => {
    setOpenProcessHistoryModal(!openProcessHistoryModal);
  };

  const toggleEditDocumentProcessModal = (isChangeApprover?: boolean) => {
    if (!checkPermission(PERMISSIONS.APPROVE_DOCUMENT)) return message.error(MESSAGES.NOT_PERMISSION);

    const value = !openEditDocumentProcessModal;
    setOpenEditDocumentProcessModalModal(value);
    setIsChangeApprover(isChangeApprover || false);
    if (!value) {
      setPublish(false);
      setInterpublishStatus(false);
    }
  };

  const onMenuSelect = (menu: string) => {
    if (statusQuery) {
      searchParams.set("status", statusQuery);
    }
    searchParams.set("action", menu);
    searchParams.set(
      "fileId",
      document?.docStatusValue === documentStatus.interimpublish.value &&
        isPublishStatus === documentStatus.publish.value
        ? document?.workingFileId || ""
        : document?.fileId || "",
    );
    replaceSearchParams(searchParams);
    setIsInterPublish(true);
    setSelectedMenu(menu);
  };

  const onSkip = () => {
    let nextStatus = getNextDocumentStatus(document?.docStatusId);

    const status = {
      docStatusId: nextStatus.id,
      docStatusValue: nextStatus.value,
      docStatus: nextStatus.label,
      docNotes: "",
      fileAssignedTo: JSON.stringify([]),
    };

    const fields = { ...status };
    updateDocument(fields);
  };

  const onPublish = (callback?: any) => {
    if (!checkPermission(PERMISSIONS.DEPLOY_DOCUMENT)) return message.error(MESSAGES.NOT_PERMISSION);

    if (!document.docCode) {
      CreateNotification({
        title: "Thông báo",
        body: `Vui lòng cập nhật mã tài liệu trước khi ban hành.`,
        icon: "/icons/warning.png",
      });
      return;
    }
    setPublish(true);
    toggleEditDocumentProcessModal();
    callbackRef.current = callback;
  };

  const onInterpublish = (callback?: any) => {
    if (!checkPermission(PERMISSIONS.DEPLOY_DOCUMENT)) return message.error(MESSAGES.NOT_PERMISSION);

    if (!document.docCode) {
      CreateNotification({
        title: "Thông báo",
        body: `Vui lòng cập nhật mã tài liệu trước khi ban hành tạm thời.`,
        icon: "/icons/warning.png",
      });
      return;
    }
    setInterpublishStatus(true);
    toggleEditDocumentProcessModal();
    callbackRef.current = callback;
  };

  const sendMail = (comment?: string, status?: string) => {
    const docEditors = document?.docEditors ? JSON.parse(document?.docEditors) : [];
    const docReviewers = document?.docReviewers ? document?.docReviewers : [];
    const docDetectors = document?.docDetectors ? document?.docDetectors : [];
    const docCheckers = document?.docCheckers ? document?.docCheckers : [];
    const userSendEmail = [...docEditors, ...docReviewers, ...docDetectors, ...docCheckers].map((e: any) => e.email);
    const finalUserEmail = [...userSendEmail, document?.userEmail];
    const distinctEmails = [...new Set(finalUserEmail)];
    let url = `${detailUrl}/${document?.listItemId}?action=process&fileId=${document.fileId}&index=2`;
    const template = mailtemplates.find((m: any) => m.eMailType === "PRDC");
    const subject = template.eSubject.replace("{0}", document?.Title || "").replace("{1}", status);
    const content = template.eContent
      .replace(mailRegexs.zero + ";", document?.Title || "")
      .replace(mailRegexs.one + ";", document?.docCode || "")
      .replace(mailRegexs.two + ";", comment)
      .replace(mailRegexs.three + ";", status)
      .replace(mailRegexs.four + ";", name)
      .replace(mailRegexs.five + ";", email)
      .replace(mailRegexs.siteUrl, url);

    if (status === "phê duyệt") {
      MailService.sendMailByBotit({ content, subject, toMail: distinctEmails.join(";") });
      // const message = MailService.buildMailMesssageForPeople(subject, content, "html", distinctEmails);
      // MailService.sendMail(message);
    } else {
      MailService.sendMailByBotit({ content, subject, toMail: document?.userEmail! });
      // const message = MailService.buildMailMesssage(subject, content, "html", document?.userEmail!);
      // MailService.sendMail(message);
    }
  };

  const handleOpenRejectModal = (status: string) => {
    if (!checkPermission(PERMISSIONS.APPROVE_DOCUMENT)) return message.error(MESSAGES.NOT_PERMISSION);

    setIsOpenRefuseModal(true);
    setCurrentStatus(status);
  };

  const onReject = async (text?: string, status?: string) => {
    setIsOpenRefuseModal(false);
    const fields = {
      docStatus: documentStatus.compose.label,
      docStatusId: documentStatus.compose.id,
      docStatusValue: documentStatus.compose.value,
      // approvalStatus: "reject",
    };
    await updateDocument(fields);
    sendMail(text, status);
  };

  const updateDocument = (fields: any) => {
    DocumentService.updateFileInfo(document?.id, fields).then(
      () => {
        const fileInfoFields = {
          subInfo: fields.subInfo,
          docNotes: fields.docNotes,
          docStatusId: fields.docStatusId,
          docStatusValue: fields.docStatusValue,
          docStatus: fields.docStatus,
        };
        FileService.updateFileInfo(document?.docId, fileInfoFields).then();
        CreateNotification({
          title: "Thành công",
          body: `Cập nhật tài liệu thành công.`,
          icon: "/icons/success.png",
        });
        queryClient.invalidateQueries({ queryKey: ["document-info"] });
      },
      (error) => {
        CreateNotification({
          title: "Thông báo",
          body: `Không thể cập nhật thông tin tài liệu.`,
          icon: "/icons/error.png",
        });
        console.error(error);
      },
    );
  };

  const getBackLink = () => {
    // let parameter = "";
    // if (docParentFolderId) {
    //   parameter = `?folderId=${docParentFolderId}`;
    //   if (isPublishStatus === "publish") parameter += `&status=publish`;
    // } else {
    //   if (isPublishStatus === "publish") parameter += `?status=publish`;
    // }

    // return history.push(`${Routes.document.path}${parameter}`);

    if (history.action === "PUSH") {
      history.goBack();
    } else {
      let parameter = "";
      if (docParentFolderId) {
        parameter = `?folderId=${docParentFolderId}`;
        if (isPublishStatus === "publish") parameter += `&status=publish`;
      } else {
        if (isPublishStatus === "publish") parameter += `?status=publish`;
      }

      return history.push(`${Routes.document.path}${parameter}`);
    }
  };

  const goToPermission = async () => {
    try {
      var result = await PermissionService.getDocumentPermissons(document?.fileId, listId);

      if (result.count > 0) setCurrentDocumentPermissionProcess(result.data);

      setOpenPermissionModal(!openPermissionModal);
    } catch (error: any) {
      CreateNotification({
        title: "Không thể truy cập phân quyền",
        body: error.message,
        icon: "/icons/error.png",
      });
      console.error(error);
    }
  };

  const handleHidePermissionModal = () => {
    setOpenPermissionModal(!openPermissionModal);
  };

  const onDocumentUpdated = (document: any, isUpdateLoading: boolean = false) => {
    toggleEditDetailModal();
    searchParams.delete("mode");
    replaceSearchParams(searchParams);
    if (+id <= 0) {
      history.replace(
        Routes.documentDetail.path.replace(":id", document.docId) +
          `?action=${userAction.file}&fileId=${document.fileId}`,
      );
      setSelectedMenu(userAction.file);
      setIsUpdateLoading(isUpdateLoading);
    } else if (!document.IsCustomEditor) {
      history.replace(
        Routes.documentDetail.path.replace(":id", document.docId) +
          `?action=${userAction.process}&fileId=${document.fileId}`,
      );
      setSelectedMenu(userAction.process);
    }
  };

  const onContentUpdated = () => {
    toggleEditDocumentProcessModal();
    if (callbackRef.current && typeof callbackRef.current === "function") callbackRef.current();
  };

  const openUrl = async () => {
    const fileId = isPublish ? document.workingFileId : document.fileId;
    const file = await FileService.getFile(fileId, listId);

    window.open(file.url, "_blank");
  };

  return (
    <>
      <EditorContentPermissionContextProvider context={ctx}>
        <Header />
        <Container fluid className="document-detail">
          {isLoading || isUpdateLoading ? (
            <Spinner />
          ) : (
            <>
              {document ? (
                <>
                  <Row className="mt-2 ">
                    <Col className="justify-content-left">
                      <div className="PageHead">
                        <div className="title">
                          <div
                            title={"Trở về trang Danh sách tài liệu"}
                            className="btnBack"
                            onClick={() => getBackLink()}
                          >
                            <Icon.ArrowLeft />
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col sm={10} md={10}>
                      <ListGroup as="ul" horizontal>
                        {document && document.IsCustomEditor && (
                          <ListGroup.Item
                            key="file"
                            className={`menu-item ${selectedMenu === "file" ? "active" : ""}`}
                            onClick={() => {
                              onMenuSelect("file");
                            }}
                          >
                            {"Nội dung văn bản"}
                          </ListGroup.Item>
                        )}
                        <ListGroup.Item
                          key="info"
                          className={`menu-item ${selectedMenu === "info" ? "active" : ""}`}
                          onClick={() => {
                            onMenuSelect("info");
                          }}
                        >
                          {"Thông tin tài liệu"}
                        </ListGroup.Item>
                        {mode !== userAction.new && (
                          <>
                            <ListGroup.Item
                              key="process"
                              className={`menu-item ${selectedMenu === "process" ? "active" : ""}`}
                              onClick={() => {
                                onMenuSelect("process");
                              }}
                            >
                              Chu trình xử lý
                            </ListGroup.Item>
                            <>
                              <ListGroup.Item
                                key="link"
                                className={`menu-item ${selectedMenu === "link" ? "active" : ""}`}
                                onClick={() => {
                                  onMenuSelect("link");
                                }}
                              >
                                Tài liệu liên kết
                                <Badge count={totalDocLink || 0} offset={[16, -22]} />
                              </ListGroup.Item>
                              {document.IsCustomEditor && (
                                <ListGroup.Item
                                  key="comment"
                                  className={`menu-item ${selectedMenu === "comment" ? "active" : ""}`}
                                  onClick={() => {
                                    onMenuSelect("comment");
                                  }}
                                >
                                  {"Danh sách ý kiến"}
                                </ListGroup.Item>
                              )}
                            </>
                          </>
                        )}
                      </ListGroup>
                    </Col>
                  </Row>
                  <Row className="mx-0">
                    <Col sm={12} md={12} className="py-3">
                      {document && document.IsCustomEditor && selectedMenu === "file" && <Editor />}
                      {selectedMenu === "info" &&
                        (mode === userAction.edit || mode === userAction.new ? (
                          <EditDocumentDetailInfo
                            data={document}
                            copiedTemplateId={templateId}
                            folderId={docParentFolderId}
                            listId={listId}
                            isCustomEditor={document.IsCustomEditor}
                            onClose={onCloseEditDocument}
                            onUpdated={onDocumentUpdated}
                            onPublish={onPublish}
                            onInterpublish={onInterpublish}
                            toggleEditDocumentProcessModal={toggleEditDocumentProcessModal}
                          />
                        ) : (
                          <DocumentInfo
                            document={document}
                            documentHis={documentHistoy}
                            toggleEditDetailModal={toggleEditDetailModal}
                            goToPermission={goToPermission}
                            isReadOnly={isPublishStatus || !hasWritePermission}
                            isReadOnlyPermission={!hasWritePermission}
                            isPublishStatus={isPublishStatus}
                            isUpdateLoading={isUpdateLoading}
                          />
                        ))}
                      {mode !== userAction.new && selectedMenu === "process" && (
                        <DocumentProcess
                          isReadOnly={isPublishStatus || !hasWritePermission}
                          documentHis={documentHistoy}
                          document={document}
                          onPublish={onPublish}
                          onInterpublish={onInterpublish}
                          onReject={handleOpenRejectModal}
                          onSkip={onSkip}
                          isInterpublish={isInterpublish}
                          toggleEditDocumentProcessModal={toggleEditDocumentProcessModal}
                          setSelectedMenu={setSelectedMenu}
                          toggleProcessHistoryModal={toggleProcessHistoryModal}
                          openUrl={openUrl}
                        />
                      )}
                      {mode !== userAction.new && (
                        <DocumentLink
                          isReadOnly={isPublishStatus || !hasWritePermission}
                          document={document}
                          toggleProcessHistoryModal={toggleProcessHistoryModal}
                          visible={selectedMenu === "link"}
                        />
                      )}
                      {mode !== userAction.new && selectedMenu === "comment" && <CommentList />}
                    </Col>
                  </Row>
                </>
              ) : (
                <div className="d-flex align-items-center justify-content-center vh-100">
                  <div>
                    <div className="text-center"></div>
                    <div className="text-center pt-3">
                      <Button
                        onClick={() => history.push(`${Routes.document.path}`)}
                        variant="info"
                        disabled={isUpdateLoading}
                      >
                        {"Quay lại"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Container>

        <DepartmentList
          id={document?.fileId}
          title={getTitleForDocument(document)}
          listId={listId}
          isDocumentLibrary={true}
          linkProcess={currentDocumentPermissionProcess}
          filterScopes={filterScopes}
          filterTypes={filterTypes}
          isOpen={openPermissionModal}
          onClose={handleHidePermissionModal}
          document={document}
          isPublishStatus={isPublishStatus}
        />

        <EditDocumentDetailModal onClose={toggleEditDetailModal} onUpdated={onDocumentUpdated} data={document} />
        <EditDocumentProcessModal
          isPublish={isPublish}
          isInterpublishStatus={interpublishStatus}
          onClose={toggleEditDocumentProcessModal}
          isOpen={openEditDocumentProcessModal}
          onUpdated={onContentUpdated}
          isChangeApprover={isChangeApprover}
          data={document}
          currentUser={user.current}
          onInterPublish={onInterPublish}
        />
        <AcceptRefuseProcessModal
          isOpen={isOpenRefuseModal}
          onClose={() => setIsOpenRefuseModal(false)}
          action="refuse"
          status={currentStatus}
          handleConfirm={onReject}
        />
      </EditorContentPermissionContextProvider>
    </>
  );
};
